import {identity} from 'lodash';
import React, {ReactElement, ReactNode, useRef} from 'react';
import styled from 'styled-components';

import {buildHoverParentClassName} from '../../../../../libs/shared/helpers/src/browser/hoverHelpers';
import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {HandlerOf, RendererOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Icon, IconName} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {PopoverTransferProps} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../../libs/shared/ui/src/styles/mixins';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {BoxedIcon} from '../../boxedIcon';
import {Skeleton} from '../../skeleton';
import {
  getCellBackgroundColor,
  getCellIndexStyles,
  getCellTextColor,
  getTableCellAvatarSize,
} from '../helpers/tableCellHelpers';
import {getWidthSettingsStyles} from '../tableColumn';
import {StyledActionsDiv, StyledActionsIcon} from './settingsCommonCellStyles';
import {TableCell, TableCellProps, TableCellStyleProps} from './tableCell';

/*
 * Props.
 */

export interface IconCellProps<S> extends TableCellProps<S> {
  iconNode?: ReactNode;
  iconName?: IconName;
  iconSize?: VisualSizesEnum;
  iconColor?: string;
  backgroundColor?: string;
  borderColor?: string;
  useNormalFontWeight?: boolean;
  renderActionsDropdown?: RendererOf<{
    modalProps: PopoverTransferProps;
    value: S | undefined;
  }>;
  onActionsDropdownOpened?: HandlerOf<boolean>;
  hasHover?: boolean;
  warningText?: ReactNode;
  warningTooltip?: ReactNode;
}

/*
 * Styles.
 */

const StyledIconCellContainerDiv = styled.div<TableCellStyleProps>`
  padding: 0 12px;
  overflow: hidden;
  white-space: nowrap;
  display: flex;
  flex-flow: row;
  height: 100%;
  align-items: center;

  ${(p) => getCellBackgroundColor(p.$isHovering, p.$isSelected)}
  ${(p) => getWidthSettingsStyles(p.$settings?.cellWidth, p.$index)}
  ${(p) =>
    getCellIndexStyles(
      p.$index,
      p.$isSelected,
      p.$shouldRemoveTopBorderRadius,
      p.$shouldRemoveBottomBorderRadius,
    )};
`;

interface StyledIconDivProps {
  $iconSize: number;
}

const StyledIconContainerDiv = styled.div<StyledIconDivProps>`
  flex: 0 0 ${(p) => p.$iconSize}px;
  margin-right: 12px;
`;

interface StyledLabelDivProps {
  $isSelected: boolean;
  $useNormalFontWeight: boolean;
}

const StyledLabelDiv = styled.div<StyledLabelDivProps>`
  align-items: center;
  flex: 1;
  font-size: ${fontSizes.medium};
  font-weight: ${(p) => (p.$useNormalFontWeight ? fontWeights.normal : fontWeights.bold)};

  ${ellipsis()};
  ${(p) => getCellTextColor(p.$isSelected)}
`;

const StyledWarningTooltipCoordinator = styled(TooltipCoordinator)`
  color: ${(p) => p.theme.palette.red.shade40};
  display: flex;
  flex-direction: row;
  font-size: ${fontSizes.verySmall};
  align-items: center;
  & > svg {
    margin-right: 4px;
  }
  & > span {
    ${ellipsis()};
  }
`;

const StyledTooltipContainerDiv = styled.div`
  padding-right: 8px;
`;

/*
 * Component.
 */

export const IconCell = <T extends {} = string>(props: IconCellProps<T>): ReactElement => {
  const theme = useUserTheme();
  const {
    isSelected,
    isHovering,
    isLoading,
    size,
    settings,
    index,
    iconSize,
    iconName,
    iconNode,
    backgroundColor = theme.foreground,
    iconColor = theme.greys.shade70,
    borderColor = theme.greys.shade30,
    shouldShowStickyGradient,
    shouldRemoveTopBorderRadius,
    shouldRemoveBottomBorderRadius,
    useNormalFontWeight,
    renderActionsDropdown,
    onActionsDropdownOpened,
    warningText,
    warningTooltip,
  } = props;

  const iconContainerSizePx = getTableCellAvatarSize(size, iconSize);
  const iconRef = useRef<HTMLDivElement>(null);

  const renderWarningTooltip = () => <StyledTooltipContainerDiv>{warningTooltip}</StyledTooltipContainerDiv>;

  return (
    <TableCell<T>
      isSelected={isSelected}
      isHovering={isHovering}
      isLoading={isLoading}
      size={size}
      settings={settings}
      index={index}
      value={props.value}
      shouldShowStickyGradient={shouldShowStickyGradient}
      shouldRemoveTopBorderRadius={shouldRemoveTopBorderRadius}
      shouldRemoveBottomBorderRadius={shouldRemoveBottomBorderRadius}
      renderLoading={() => (
        <StyledIconCellContainerDiv
          $settings={settings}
          $isHovering={false}
          $isSelected={Boolean(isSelected)}
          $index={index}
          $shouldRemoveTopBorderRadius={shouldRemoveTopBorderRadius}
          $shouldRemoveBottomBorderRadius={shouldRemoveBottomBorderRadius}
        >
          <StyledIconContainerDiv $iconSize={iconContainerSizePx}>
            <Skeleton
              height={`${iconContainerSizePx}px`}
              width={`${iconContainerSizePx}px`}
              borderRadius="50%"
              scheme={settings?.loading?.style}
            />
          </StyledIconContainerDiv>
          <StyledLabelDiv $isSelected={false} $useNormalFontWeight={useNormalFontWeight ?? false}>
            <Skeleton height="16px" width="140px" borderRadius="4px" scheme={settings?.loading?.style} />
          </StyledLabelDiv>
        </StyledIconCellContainerDiv>
      )}
      renderCell={(value) => (
        <StyledIconCellContainerDiv
          $settings={settings}
          $isHovering={Boolean(isHovering)}
          $index={index}
          $isSelected={Boolean(isSelected)}
          $shouldRemoveTopBorderRadius={shouldRemoveTopBorderRadius}
          $shouldRemoveBottomBorderRadius={shouldRemoveBottomBorderRadius}
          className={buildHoverParentClassName()}
        >
          <StyledIconContainerDiv $iconSize={iconContainerSizePx}>
            {iconNode || (
              <BoxedIcon
                name={iconName || 'content'}
                backgroundColor={backgroundColor}
                color={iconColor}
                borderColor={borderColor}
                borderStyle="solid"
                size={iconSize}
              />
            )}
          </StyledIconContainerDiv>
          <StyledLabelDiv
            $isSelected={Boolean(isSelected)}
            $useNormalFontWeight={useNormalFontWeight ?? false}
          >
            <>{value}</>
            {warningText && (
              <StyledWarningTooltipCoordinator render={renderWarningTooltip} isMultiline maxWidth={240}>
                <Icon name="warningFilledTiny" color={theme.palette.red.shade40} />
                <span>{warningText}</span>
              </StyledWarningTooltipCoordinator>
            )}
          </StyledLabelDiv>
          {renderActionsDropdown && onActionsDropdownOpened && (
            <StyledActionsDiv
              $isSelected={props.isSelected}
              $hasHover={Boolean(props.hasHover || props.isHovering)}
            >
              <StyledActionsIcon
                label={<Trans id="settingstable-actions">Actions</Trans>}
                iconName="moreVertical"
                color={props.isSelected ? theme.greys.white : theme.greys.shade70}
                hoverActiveColor={props.isSelected ? theme.greys.white : theme.greys.shade80}
                hoverColor={props.isSelected ? theme.greys.white : theme.greys.shade80}
                isOpen={props.hasHover}
                hasHover={props.hasHover}
                onClick={(e) => {
                  e.preventDefault();
                  onActionsDropdownOpened(true);
                }}
                ref={iconRef}
              />
              {props.hasHover &&
                renderActionsDropdown({
                  modalProps: {
                    anchor: iconRef,
                    onRequestClose: () => onActionsDropdownOpened(false),
                    wrap: identity,
                  },
                  value: props.value,
                })}
            </StyledActionsDiv>
          )}
        </StyledIconCellContainerDiv>
      )}
    />
  );
};
